import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { InputTable } from '../../../../components/InputTable/InputTable.component';
import { ErrorList, InputTableColumns } from '../../../../components/InputTable/InputTable.type';
import { HorizonalTabs } from '../../../../components/Tabs/HorizontalTabs/HorizonalTabs.component';
import {
  ToleranceRangeChange,
  ToleranceRangeCriterion,
  ToleranceRangeResponse,
  ToleranceRangeRow,
  ToleranceRangeTask,
  ToleranceRangeTaskGroup,
  ToleranceType,
} from './ToleranceRange.types';
import { useLayoutContext } from '../../../../Context/Layout.context';
import { ToleranceLabels } from './Subcomponents/ToleranceLabels.components';
import {
  getExamSheetProcessLimitToleranceRange,
  getAssessmentSessionProcessLimitToleranceRange,
  putExamTaskGroupProcessLimitToleranceRange,
  putAssessmentSessionProcessLimitToleranceRange,
} from '../../../../services/exams/assessmentSession/assessments.service';
import { AxiosResponse } from 'axios';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { AssessmentSessionProcessTypeId } from '../../../../services/exams/assessmentSession/assessments.types';
import { getProcessLabel } from '../../../../utils/assessmentSessionProcess';
import { InputSelection } from '../../../../components/InputTable/InputSelection/InputSelection.component';
import { InputTextField } from '../../../../components/InputTable/InputTextField/InputTextField.component';
import { blockIconForInput } from '../../../../components/EditSession/BlockItemForInput';

export type processType = AssessmentSessionProcessTypeId.Qualification | AssessmentSessionProcessTypeId.Control;

interface ToleranceRangeProps {
  processType: processType;
  id: string;
  onClose: () => void;
  isExaminerView?: boolean;
  onChange?: (isDataChanged: boolean) => void;
}
export const ToleranceRange: React.FC<ToleranceRangeProps> = ({ processType, id, isExaminerView, onClose, onChange }) => {
  const [apiResponse, setApiResponse] = useState<ToleranceRangeResponse>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [items, setItems] = useState<ToleranceRangeRow[]>([]);
  const [index, setIndex] = useState<number>(0);
  const [isNotRequired, setIsNotRequired] = useState<boolean>(false);
  const [invalidType, setInvalidType] = useState<ErrorList[]>([]);
  const { openAlert } = useLayoutContext();
  const [receivedItems, setReceivedItems] = useState<string>('');
  const isEdited = receivedItems ? !_.isEqual(JSON.parse(receivedItems), items) : false;
  const [subIndex, setSubInex] = useState<number>(0);
  const [reload, setReload] = useState<boolean>(false);
  const [isSomeRangeActive, setIsSomeRangeActive] = useState<boolean>(false);
  const { t } = useTranslation();

  const handleOnClick = (index: number): void => {
    if (!isEdited) {
      setIndex(index);
    } else {
      setSubInex(index);
    }
  };

  const columns: InputTableColumns[] = [
    {
      id: 'label',
      label: t('sessionsParameters:controllToleranceRange.table.label'),
    },
    {
      id: 'type',
      label: t('sessionsParameters:controllToleranceRange.table.type'),
    },
    {
      id: 'value',
      label: t('sessionsParameters:controllToleranceRange.table.value'),
    },
  ];

  const getHorizonalTab = () => {
    return apiResponse &&
      apiResponse.examTaskGroup &&
      Array.isArray(apiResponse.examTaskGroup) &&
      apiResponse.examTaskGroup.length > 1 ? (
      <div
        style={{
          width: '100%',
          height: 50,
          marginTop: -8,
          marginBottom: 0,
        }}
      >
        <HorizonalTabs
          onClick={handleOnClick}
          selectedItemIndex={index}
          items={apiResponse.examTaskGroup.map((e) => {
            return { label: e.examTaskGroupName };
          })}
        />
      </div>
    ) : undefined;
  };

  const handleValueCallback = useCallback(
    (key: keyof ToleranceRangeRow, value: number | null | boolean, id: string | number) => {
      setItems((state: ToleranceRangeRow[]) => {
        return state.map((s: ToleranceRangeRow) => {
          if (s.id === id) {
            s[key] = value as never;
          }
          return s;
        });
      });
    },
    [items, setItems],
  );

  const validate = (row: ToleranceRangeRow): ErrorList[] => {
    const errorList: ErrorList[] = [];
    const { isActive, type, value, id } = row;
    if (isActive) {
      if (type === ToleranceType.RelativeDifference) {
        if (value < -10 || value > 10 || value === null) {
          errorList.push({
            id: id,
            type: 'value',
            value: type,
          });
        }
      }
      if (type === ToleranceType.AbsoluteDifference) {
        if (value < 0 || value > 10 || value === null) {
          errorList.push({
            id: id,
            type: 'value',
            value: type,
          });
        }
      }
    }
    return errorList;
  };

  const valueValidation = (row: ToleranceRangeRow): boolean => {
    const isValid = validate(row);
    if (!isValid.length) {
      setInvalidType((prev) => prev.filter((e) => e.id !== row.id));
      return isValid.length > 0;
    } else {
      setInvalidType((prev) => [...prev, ...isValid]);
      return isValid.length > 0;
    }
  };

  const getValidationMessage = (error: ErrorList): string => {
    const message = "Podana wartość jest nieprawidłowa, prawidłowa wartość powinna mieścić się w zakesie liczb od -10 do 10, z jednym miejscem po przecinku";
    if (error.value === 0) {
      return message;
    } else {
      return "Podana wartość jest nieprawidłowa, prawidłowa wartość powinna mieścić się w zakesie liczb od 0 do 10, z jednym miejscem po przecinku";
    }
  };

  const getRowComponent = (
    type: string,
    value: ToleranceRangeRow,
    isValid: boolean,
  ): React.ReactNode | string | null => {
    switch (type) {
      case 'label':
        return (
          <ToleranceLabels
            handleValueChange={handleValueCallback}
            validation={valueValidation}
            value={value}
            type={type}
          />
        );
      case 'type':
        return !value.hasSingleItemChildren ? (
          <InputSelection<ToleranceRangeRow>
            handleValueChange={handleValueCallback}
            validation={valueValidation}
            value={value[type]}
            rowKey={type}
            row={value}
            idRow={value.id}
            items={['Różnica ocen', 'Bezwzględna różnica ocen']}
            inputLabel={'Wybierz rodzaj tolerancji'}
            ariaLabel={'Wybierz rodzaj tolerancji'}
            disabled={!value.isActive}
          />
        ) : null;
      case 'value':
        return !value.hasSingleItemChildren ? (
          <div style={{ margin: 8 }}>
            <InputTextField<ToleranceRangeRow>
              handleValueChange={handleValueCallback}
              validation={valueValidation}
              value={value}
              rowKey={type}
              isValid={isValid}
              idRow={value.id}
              inputLabel={'Zakres tolerancji'}
              ariaLabel={`Wybierz zakres tolerancji`}
              inputProps={{
                step: '0.5',
              }}
              error={isValid}
              disabled={!value.isActive}
              required
              type="number"
              decimal={1}
              InputProps={{
                startAdornment: blockIconForInput(!value.isActive, { marginRight: '0px' }),
              }}
            />
          </div>
        ) : null;
      default:
        return null;
    }
  };

  const processTypeId = processType.toString();

  useEffect((): void => {
    if (isExaminerView) {
      getAssessmentSessionProcessLimitToleranceRange(processTypeId)
        .then((res: AxiosResponse<ToleranceRangeResponse>) => {
          setApiResponse(res.data);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      getExamSheetProcessLimitToleranceRange(id, processTypeId)
        .then((res: AxiosResponse<ToleranceRangeResponse>) => {
          setApiResponse(res.data);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [isExaminerView, processType, processTypeId, id, reload]);

  const handleDiscardCallback = useCallback(() => {
    onClose();
  }, [onClose]);

  const getCriteriumMaxScore = (criterion: ToleranceRangeCriterion[]): number => {
    let maxScore = 0;
    if (criterion.length > 0) {
      criterion.forEach((criterion: ToleranceRangeCriterion) => {
        maxScore += Math.max(...criterion.scores);
      });
    }
    return maxScore;
  };

  const getSumScore = (
    fieldType: 'examTaskGroup' | 'task',
    dataSource: ToleranceRangeTask | ToleranceRangeTaskGroup,
  ): number[] => {
    if (fieldType === 'examTaskGroup') {
      let maxScore = 0;
      const data = dataSource as ToleranceRangeTaskGroup;
      data.task.forEach((task: ToleranceRangeTask) => {
        maxScore += getCriteriumMaxScore(task.criterion);
      });

      return [maxScore];
    }

    const data = dataSource as ToleranceRangeTask;
    return [getCriteriumMaxScore(data.criterion)];
  };

  useEffect((): void => {
    if (apiResponse) {
      const conversion: ToleranceRangeRow[] = [];
      if (Array.isArray(apiResponse.examTaskGroup)) {
        if (apiResponse.examTaskGroup[index].isProcessRequired) {
          const examTaskGroupId = apiResponse.examTaskGroup[index].examTaskGroupId;
          const hasTaskGroupToleranceRange: boolean = apiResponse.examTaskGroup[index].toleranceRange !== null;
          conversion.push({
            id: apiResponse.examTaskGroup[index].examTaskGroupId,
            fieldType: 'examTaskGroup',
            label: apiResponse.examTaskGroup[index].examTaskGroupName,
            value: hasTaskGroupToleranceRange
              ? apiResponse.examTaskGroup[index].toleranceRange!.toleranceRangeValue
              : 0,
            type: hasTaskGroupToleranceRange
              ? apiResponse.examTaskGroup[index].toleranceRange!.toleranceRangeType
              : ToleranceType.AbsoluteDifference,
            isActive: hasTaskGroupToleranceRange ? apiResponse.examTaskGroup[index].toleranceRange!.isActive : false,
            parentId: id,
            isProcessRequired: apiResponse.examTaskGroup[index].isProcessRequired,
            hasSingleItemChildren: apiResponse.examTaskGroup[index].task.length === 1,
            scores: getSumScore('examTaskGroup', apiResponse.examTaskGroup[index]),
          });

          const tasks = apiResponse.examTaskGroup[index].task;
          apiResponse.examTaskGroup[index].task.forEach((t: ToleranceRangeTask, taskIndex) => {
            const taskId = t.taskId;
            conversion.push({
              id: taskId,
              fieldType: 'task',
              label: t.taskNumber,
              value: t.toleranceRange ? t.toleranceRange.toleranceRangeValue : 0,
              type: t.toleranceRange ? t.toleranceRange.toleranceRangeType : ToleranceType.AbsoluteDifference,
              parentId: examTaskGroupId,
              isActive: t.toleranceRange ? t.toleranceRange.isActive : false,
              hasSingleItemChildren: t.criterion.length === 1,
              scores: getSumScore('task', tasks[taskIndex]),
            });
            t.criterion.forEach((criterion: ToleranceRangeCriterion) => {
              conversion.push({
                id: criterion.criterionId,
                fieldType: 'criterion',
                label: criterion.criterionName,
                value: criterion.toleranceRange ? criterion.toleranceRange.toleranceRangeValue : 0,
                type: criterion.toleranceRange
                  ? criterion.toleranceRange.toleranceRangeType
                  : ToleranceType.AbsoluteDifference,
                isActive: criterion.toleranceRange ? criterion.toleranceRange.isActive : false,
                parentId: taskId,
                hasSingleItemChildren: false,
                scores: criterion.scores,
              });
            });
            setIsNotRequired(false);
          });
        } else {
          setIsNotRequired(true);
        }
      } else {
        if (apiResponse.examTaskGroup.isProcessRequired) {
          const examTaskGroupId = apiResponse.examTaskGroup.examTaskGroupId;
          conversion.push({
            id: examTaskGroupId,
            fieldType: 'examTaskGroup',
            label: apiResponse.examTaskGroup.examTaskGroupName,
            value: apiResponse.examTaskGroup.toleranceRange
              ? apiResponse.examTaskGroup.toleranceRange.toleranceRangeValue
              : 0,
            type: apiResponse.examTaskGroup.toleranceRange
              ? apiResponse.examTaskGroup.toleranceRange.toleranceRangeType
              : ToleranceType.AbsoluteDifference,
            parentId: id ?? '11222333',
            isActive: apiResponse.examTaskGroup.toleranceRange
              ? apiResponse.examTaskGroup.toleranceRange.isActive
              : false,
            isProcessRequired: apiResponse.examTaskGroup.isProcessRequired,
            hasSingleItemChildren: apiResponse.examTaskGroup.task.length === 1,
            scores: getSumScore('examTaskGroup', apiResponse.examTaskGroup),
          });

          const tasks = apiResponse.examTaskGroup.task;
          apiResponse.examTaskGroup.task.forEach((t: ToleranceRangeTask, taskIndex: number) => {
            const taskId = t.taskId;
            conversion.push({
              id: taskId,
              fieldType: 'task',
              label: t.taskNumber,

              value: t.toleranceRange ? t.toleranceRange.toleranceRangeValue : 0,
              type: t.toleranceRange ? t.toleranceRange.toleranceRangeType : ToleranceType.AbsoluteDifference,
              parentId: examTaskGroupId,
              isActive: t.toleranceRange ? t.toleranceRange.isActive : false,
              hasSingleItemChildren: t.criterion.length === 1,
              scores: getSumScore('task', tasks[taskIndex]),
            });
            t.criterion.forEach((criterion: ToleranceRangeCriterion) => {
              conversion.push({
                id: criterion.criterionId,
                fieldType: 'criterion',
                label: criterion.criterionName,
                value: criterion.toleranceRange ? criterion.toleranceRange.toleranceRangeValue : 0,
                type: criterion.toleranceRange
                  ? criterion.toleranceRange.toleranceRangeType
                  : ToleranceType.AbsoluteDifference,
                isActive: criterion.toleranceRange ? criterion.toleranceRange.isActive : false,
                parentId: taskId,
                hasSingleItemChildren: false,
                scores: criterion.scores,
              });
            });
          });
          setIsNotRequired(false);
        } else {
          setIsNotRequired(true);
        }
      }
      setItems(conversion);
      setReceivedItems(JSON.stringify(conversion));
    }
  }, [apiResponse, index]);

  const headerText = `${t('sessionsParameters:controllToleranceRange.header')}: ${getProcessLabel(processType).label.toLowerCase()}`;

    useEffect(() => {
      onChange && onChange(isEdited);
    }, [isEdited]);

  const handleOnSave = useCallback(() => {
    const examTaskGroupId = items.find((i: ToleranceRangeRow) => i.fieldType === 'examTaskGroup')!.id;
    const data: ToleranceRangeChange = {
      toleranceRange: items.find((i: ToleranceRangeRow) => i.fieldType === 'examTaskGroup')!.hasSingleItemChildren
        ? null
        : {
            toleranceRangeValue: items.find((i: ToleranceRangeRow) => i.fieldType === 'examTaskGroup')!.value,
            toleranceRangeType: items.find((i: ToleranceRangeRow) => i.fieldType === 'examTaskGroup')!.type,
            isActive: items.find((i: ToleranceRangeRow) => i.fieldType === 'examTaskGroup')!.isActive,
          },
      task: items
        .filter((i: ToleranceRangeRow) => i.fieldType === 'task')
        .map((e: ToleranceRangeRow) => {
          return {
            taskId: e.id,
            toleranceRange: e.hasSingleItemChildren
              ? null
              : {
                  toleranceRangeValue: e.value,
                  toleranceRangeType: e.type,
                  isActive: e.isActive,
                },
            criterion: items
              .filter((i: ToleranceRangeRow) => i.fieldType === 'criterion' && i.parentId === e.id)
              .map((e: ToleranceRangeRow) => {
                return {
                  criterionId: e.id,
                  toleranceRange: {
                    toleranceRangeValue: e.value,
                    toleranceRangeType: e.type,
                    isActive: e.isActive,
                  },
                };
              }),
          };
        }),
    };
    if (isEdited && !isExaminerView) {
      putExamTaskGroupProcessLimitToleranceRange(examTaskGroupId, processTypeId, data)
        .then((res: AxiosResponse<boolean>) => {
          if (res.data) {
            openAlert('success');
            setReload((state) => !state);
            setReceivedItems(JSON.stringify(items));
          } else openAlert('error');
        })
        .catch(() => openAlert('error'));
    } else {
      putAssessmentSessionProcessLimitToleranceRange(processTypeId, data)
        .then((res: AxiosResponse<boolean>) => {
          if (res.data) {
            openAlert('success');
            setReload((state) => !state);
            setReceivedItems(JSON.stringify(items));
          } else openAlert('error');
        })
        .catch(() => openAlert('error'));
    }
  }, [items, isEdited, isExaminerView, processTypeId]);

  useEffect(() => {
    setIsSomeRangeActive(items.length > 0 && items.some((e) => e.isActive));
  }, [items]);

  return (
    <>
      <InputTable<ToleranceRangeRow>
        columns={!isNotRequired ? columns : []}
        emptyMessage="Brak danych"
        parameters={!isNotRequired ? items : []}
        items={!isNotRequired ? items : []}
        keyParam={'id'}
        invalidType={invalidType}
        headerText={headerText}
        onCancel={handleDiscardCallback}
        onSave={handleOnSave}
        getValidationMessage={getValidationMessage}
        getRowComponent={getRowComponent}
        edited={isEdited && isSomeRangeActive}
        childrenComponent={getHorizonalTab()}
        containerHeight={isExaminerView ? 410 : 380}
        isLoading={isLoading}
        isNotRequiredMessage={
          isNotRequired
            ? `W tej grupie zadań ${getProcessLabel(processType).label.toLowerCase()} nie jest wymagany`
            : undefined
        }
      />
    </>
  );
};
